import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { createPortal } from 'react-dom';
import ObliqueIcon from '@/components/icons/ObliqueIcon';
import { useAuth } from '@/contexts/AuthContext';
import { useCompany } from '@/contexts/CompanyContext';
import { useTenant } from '@/contexts/TenantContext';
import { useTheme } from '@/contexts/ThemeContext';
import WorkspaceAvatar from '@/components/common/WorkspaceAvatar';

/**
 * ═══════════════════════════════════════════════════════════════════════════
 * Oblique Design System — ProfileMenu
 * ═══════════════════════════════════════════════════════════════════════════
 * 
 * Reusable Profile & Workspace Switcher popup.
 * 
 * Usage:
 *   import { ProfileMenu } from '@/core/oblique-ui'
 *   
 *   <ProfileMenu 
 *      isOpen={isOpen} 
 *      onClose={() => setIsOpen(false)} 
 *      onCreateTenant={() => setIsCreateTenantModalOpen(true)}
 *   />
 */

export function ProfileMenu({ isOpen, onClose, onCreateTenant }) {
    const navigate = useNavigate();
    const { user, userData, userRole, logout } = useAuth();
    const { currentCompany } = useCompany();
    const { currentTenant, tenants, switchTenant } = useTenant();
    const { theme } = useTheme();

    const activeLogo = theme === 'dark'
        ? (currentCompany?.darkLogoUrl || currentTenant?.darkLogoUrl || currentCompany?.logoUrl || currentTenant?.lightLogoUrl)
        : (currentCompany?.lightLogoUrl || currentTenant?.lightLogoUrl || currentCompany?.logoUrl || currentTenant?.darkLogoUrl);
    
    const [profileView, setProfileView] = useState('main'); // 'main' | 'settings'
    const [isTenantSwitcherOpen, setIsTenantSwitcherOpen] = useState(false);

    if (!isOpen || typeof document === 'undefined') return null;

    const handleLogout = async () => {
        try {
            await logout();
            navigate('/auth/login');
        } catch (error) {
            console.error('Logout error:', error);
        }
    };

    const handleClose = () => {
        setProfileView('main');
        setIsTenantSwitcherOpen(false);
        onClose();
    };

    const handleNavigate = (path) => {
        navigate(path);
        handleClose();
    };

    return createPortal(
        <AnimatePresence>
            {isOpen && (
                <>
                    <div 
                        className="fixed inset-0 z-[var(--z-dropdown,5000)]" 
                        onClick={handleClose} 
                    />
                    <motion.div 
                        initial={{ opacity: 0, y: 12, scale: 0.95 }} 
                        animate={{ opacity: 1, y: 0, scale: 1 }} 
                        exit={{ opacity: 0, y: 12, scale: 0.95 }} 
                        transition={{ duration: 0.2, ease: [0.16, 1, 0.3, 1] }}
                        className="fixed right-4 top-14 bg-[var(--bg-surface)] border border-[var(--border)] rounded-2xl shadow-[0_16px_48px_rgba(0,0,0,0.12),0_2px_8px_rgba(0,0,0,0.06)] z-[var(--z-modal,5001)] overflow-hidden transition-[width] duration-300 ease-[cubic-bezier(0.16,1,0.3,1)]"
                        style={{ 
                            width: profileView === 'settings' ? '480px' : '320px', 
                            maxHeight: profileView === 'settings' ? '520px' : 'auto'
                        }}
                    >
                        {profileView === 'main' ? (
                            <>
                                {/* User Identity Card */}
                                <div className="p-5 border-b border-[var(--border)]">
                                    <div className="flex items-center gap-3.5 mb-3.5">
                                        <div className="w-10 h-10 rounded-full bg-[var(--accent)] text-white flex items-center justify-center text-base font-medium shrink-0 overflow-hidden">
                                            {userData?.photoURL ? (
                                                <img src={userData.photoURL} alt="Profile" className="w-full h-full rounded-full object-cover" />
                                            ) : (
                                                (userData?.displayName || userData?.name || user?.displayName || 'U')[0].toUpperCase()
                                            )}
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <div className="font-semibold text-[var(--text-primary)] text-sm whitespace-nowrap overflow-hidden text-ellipsis">
                                                {userData?.name || userData?.displayName || user?.displayName || 'User'}
                                            </div>
                                            <div className="text-xs text-[var(--text-tertiary)] font-medium mt-0.5 flex items-center gap-1.5">
                                                <span className="capitalize">{userRole || 'User'}</span>
                                                <span className="opacity-40">·</span>
                                                <span>Business Plan</span>
                                            </div>
                                        </div>
                                    </div>
                                    {/* Pill Buttons — Settings & Invite Members */}
                                    <div className="flex gap-2">
                                        <button 
                                            onClick={() => handleNavigate('/settings/preferences/profile')}
                                            className="flex items-center gap-1.5 py-1.5 px-3.5 rounded-lg border border-[var(--border)] bg-[var(--bg-surface)] cursor-pointer text-xs font-medium text-[var(--text-secondary)] transition-all duration-150 hover:bg-[var(--bg-subtle)] hover:border-[var(--border-strong)]"
                                        >
                                            <ObliqueIcon name="settings" size={14} /> Settings
                                        </button>
                                        <button 
                                            onClick={() => handleNavigate('/admin/users')}
                                            className="flex items-center gap-1.5 py-1.5 px-3.5 rounded-lg border border-[var(--border)] bg-[var(--bg-surface)] cursor-pointer text-xs font-medium text-[var(--text-secondary)] transition-all duration-150 hover:bg-[var(--bg-subtle)] hover:border-[var(--border-strong)]"
                                        >
                                            <ObliqueIcon name="person_add" size={14} /> Invite members
                                        </button>
                                    </div>
                                </div>

                                {/* User Email */}
                                <div className="py-3 px-5 border-b border-[var(--border)] flex items-center gap-2.5">
                                    <div className="text-[13px] text-[var(--text-secondary)] font-medium flex-1 overflow-hidden text-ellipsis whitespace-nowrap">
                                        {userData?.email || user?.email}
                                    </div>
                                </div>

                                {/* Active Tenant Switcher */}
                                <div className="p-3">
                                    <div className="text-[10px] font-semibold text-[var(--text-tertiary)] uppercase py-2 px-3 tracking-[0.08em]">
                                        Active Account
                                    </div>
                                    <div 
                                        onClick={() => {
                                            if (tenants.length > 1 || userRole === 'superadmin') {
                                                setIsTenantSwitcherOpen(!isTenantSwitcherOpen);
                                            }
                                        }}
                                        className={`mx-2 py-2.5 px-3 rounded-xl bg-[var(--bg-muted)] border border-[var(--border)] flex items-center gap-3 transition-colors duration-200 ${(tenants.length > 1 || userRole === 'superadmin') ? 'cursor-pointer hover:border-[var(--accent)]' : 'opacity-80 cursor-default'}`}
                                    >
                                        <WorkspaceAvatar 
                                            workspace={{
                                                ...currentCompany,
                                                ...currentTenant,
                                                name: (currentTenant?.name && currentTenant?.name !== 'Primary Workspace' && currentTenant?.name !== 'Primary Organization' ? currentTenant.name.replace(' (Fallback)', '') : currentCompany?.name) || 'The Scale Agency',
                                                domain: currentTenant?.domain || currentCompany?.domain || 'thescaleagency.com',
                                                logoUrl: currentTenant?.logoUrl || currentTenant?.lightLogoUrl || currentCompany?.logoUrl || currentCompany?.lightLogoUrl || currentCompany?.logo,
                                                lightLogoUrl: currentTenant?.lightLogoUrl || currentCompany?.lightLogoUrl,
                                                darkLogoUrl: currentTenant?.darkLogoUrl || currentCompany?.darkLogoUrl
                                            }} 
                                            size="md" 
                                            className="shadow-xs"
                                        />
                                        <div className="flex-1 overflow-hidden">
                                            <div className="text-[13px] font-semibold text-[var(--text-primary)] whitespace-nowrap overflow-hidden text-ellipsis">
                                                {(currentTenant?.name && currentTenant?.name !== 'Primary Workspace' && currentTenant?.name !== 'Primary Organization' ? currentTenant.name.replace(' (Fallback)', '') : currentCompany?.name) || 'The Scale Agency'}
                                            </div>
                                            <div className="text-[10px] text-[var(--text-tertiary)] font-medium truncate">
                                                {currentTenant?.domain || currentCompany?.domain || 'thescaleagency.com'}
                                            </div>
                                        </div>
                                        {(tenants.length > 1 || userRole === 'superadmin') && (
                                            <ObliqueIcon name="unfold_more" size={16} color="var(--text-tertiary)" />
                                        )}
                                    </div>

                                    <AnimatePresence>
                                        {isTenantSwitcherOpen && (
                                            <motion.div 
                                                initial={{ height: 0, opacity: 0 }} 
                                                animate={{ height: 'auto', opacity: 1 }} 
                                                exit={{ height: 0, opacity: 0 }} 
                                                className="overflow-hidden mt-2"
                                            >
                                                {tenants.filter(t => t.id !== currentTenant?.id).map(tenant => {
                                                    const dName = (tenant.name && tenant.name !== 'Primary Workspace' && tenant.name !== 'Primary Organization') 
                                                        ? tenant.name.replace(' (Fallback)', '') 
                                                        : (tenant.id === 'tenant-scale-agency' || tenant.domain?.includes('scale') ? 'The Scale Agency' : (currentCompany?.name || 'Workspace'));
                                                    const tItem = {
                                                        ...tenant,
                                                        name: dName,
                                                        domain: tenant.domain || (tenant.id === 'tenant-scale-agency' ? 'thescaleagency.com' : '')
                                                    };
                                                    return (
                                                        <button 
                                                            key={tenant.id}
                                                            onClick={() => { switchTenant(tenant.id); handleClose(); }}
                                                            className="w-full flex items-center gap-2.5 py-2 px-4 bg-transparent border-none cursor-pointer rounded-lg text-[13px] font-medium text-[var(--text-primary)] text-left transition-colors duration-150 hover:bg-[var(--bg-muted)]"
                                                        >
                                                            <WorkspaceAvatar workspace={tItem} size="xs" />
                                                            <span className="truncate">{dName}</span>
                                                        </button>
                                                    );
                                                })}
                                                {/* Create New Tenant Button */}
                                                {userRole === 'superadmin' && (
                                                    <button 
                                                        onClick={() => {
                                                            handleClose();
                                                            onCreateTenant?.();
                                                        }}
                                                        className="w-full flex items-center gap-2.5 py-2 px-5 bg-transparent border-t border-[var(--border)] mt-1 cursor-pointer rounded-b-lg text-[13px] font-semibold text-[var(--accent)] text-left transition-colors duration-150 hover:bg-[var(--bg-muted)]"
                                                    >
                                                        <ObliqueIcon name="add" size={16} />
                                                        <span>Add New Account</span>
                                                    </button>
                                                )}
                                            </motion.div>
                                        )}
                                    </AnimatePresence>
                                </div>

                                <div className="h-px bg-[var(--border)] mx-3 my-1" />

                                {/* Support & Walkthrough */}
                                <div className="pt-2 px-3 pb-0 flex flex-col gap-1">
                                    <button 
                                        onClick={() => {
                                            handleClose();
                                            // Handle support click, perhaps navigating to a support page or opening a modal
                                        }} 
                                        className="w-full flex items-center gap-2.5 py-2 px-2.5 bg-transparent border-none cursor-pointer rounded-lg text-[13px] font-medium text-[var(--text-secondary)] text-left transition-colors duration-150 hover:bg-[var(--bg-muted)] hover:text-[#3b82f6]"
                                    >
                                        <ObliqueIcon name="support" size={16} /> Contact Support
                                    </button>
                                    <button 
                                        onClick={() => {
                                            handleClose();
                                            localStorage.removeItem('oblique_tour_completed');
                                            window.location.reload();
                                        }} 
                                        className="w-full flex items-center gap-2.5 py-2 px-2.5 bg-transparent border-none cursor-pointer rounded-lg text-[13px] font-medium text-[var(--text-secondary)] text-left transition-colors duration-150 hover:bg-[var(--bg-muted)]"
                                    >
                                        <ObliqueIcon name="help" size={16} /> Replay Walkthrough
                                    </button>
                                </div>

                                {/* Logout */}
                                <div className="pt-2 px-3 pb-3">
                                    <button 
                                        onClick={handleLogout} 
                                        className="w-full flex items-center gap-2.5 py-2 px-2.5 bg-transparent border-none cursor-pointer rounded-lg text-[13px] font-medium text-[var(--text-secondary)] text-left transition-colors duration-150 hover:bg-[var(--bg-muted)]"
                                    >
                                        <ObliqueIcon name="logout" size={16} /> Log out
                                    </button>
                                </div>
                            </>
                        ) : (
                            /* Settings View — Oblique SS3 Style */
                            <>
                                {/* Back Header */}
                                <div className="py-3.5 px-4 border-b border-[var(--border)] flex items-center gap-2.5">
                                    <button 
                                        onClick={() => setProfileView('main')}
                                        className="flex items-center justify-center w-7 h-7 rounded-md border-none bg-transparent cursor-pointer text-[var(--text-secondary)] transition-colors duration-150 hover:bg-[var(--bg-muted)]"
                                    >
                                        <ObliqueIcon name="arrow_back" size={18} />
                                    </button>
                                    <span className="text-sm font-semibold text-[var(--text-primary)]">Settings</span>
                                </div>
                                
                                {/* Settings Categories */}
                                <div className="py-2 px-3 max-h-[420px] overflow-y-auto custom-scrollbar">
                                    {/* Account Section */}
                                    <div className="pt-1.5 px-2 pb-1 text-[10px] font-medium text-[var(--text-tertiary)] uppercase tracking-[0.06em]">Account</div>
                                    {[
                                        { icon: 'person', label: 'My Profile', path: '/settings/preferences/profile' },
                                        { icon: 'shield', label: 'Security', path: '/settings/preferences/security' },
                                        { icon: 'hub', label: 'Connections', path: '/settings/apps/connections' },
                                        { icon: 'settings', label: 'General', path: '/settings/account/general' },
                                    ].map(item => (
                                        <button 
                                            key={item.path}
                                            onClick={() => handleNavigate(item.path)}
                                            className="w-full flex items-center gap-2.5 py-2 px-2.5 bg-transparent border-none cursor-pointer rounded-lg text-[13px] font-medium text-[var(--text-primary)] text-left transition-colors duration-150 hover:bg-[var(--bg-muted)]"
                                        >
                                            <ObliqueIcon name={item.icon} size={16} color="var(--text-tertiary)" />
                                            {item.label}
                                        </button>
                                    ))}

                                    {/* Workspace Section */}
                                    <div className="pt-3 px-2 pb-1 text-[10px] font-medium text-[var(--text-tertiary)] uppercase tracking-[0.06em]">Workspace</div>
                                    {[
                                        { icon: 'groups', label: 'People', path: '/settings/users/people' },
                                        { icon: 'payments', label: 'Billing', path: '/settings/billing/subscription' },
                                    ].map(item => (
                                        <button 
                                            key={item.path}
                                            onClick={() => handleNavigate(item.path)}
                                            className="w-full flex items-center gap-2.5 py-2 px-2.5 bg-transparent border-none cursor-pointer rounded-lg text-[13px] font-medium text-[var(--text-primary)] text-left transition-colors duration-150 hover:bg-[var(--bg-muted)]"
                                        >
                                            <ObliqueIcon name={item.icon} size={16} color="var(--text-tertiary)" />
                                            {item.label}
                                        </button>
                                    ))}

                                    {/* Platform Section */}
                                    <div className="pt-3 px-2 pb-1 text-[10px] font-medium text-[var(--text-tertiary)] uppercase tracking-[0.06em]">Platform</div>
                                    {[
                                        { icon: 'verified_user', label: 'Admin Console', path: '/admin/workspace' },
                                        { icon: 'help_outline', label: 'Help Center', path: '/settings/account/general' },
                                    ].map(item => (
                                        <button 
                                            key={item.path}
                                            onClick={() => handleNavigate(item.path)}
                                            className="w-full flex items-center gap-2.5 py-2 px-2.5 bg-transparent border-none cursor-pointer rounded-lg text-[13px] font-medium text-[var(--text-primary)] text-left transition-colors duration-150 hover:bg-[var(--bg-muted)]"
                                        >
                                            <ObliqueIcon name={item.icon} size={16} color="var(--text-tertiary)" />
                                            {item.label}
                                        </button>
                                    ))}
                                </div>
                            </>
                        )}
                    </motion.div>
                </>
            )}
        </AnimatePresence>,
        document.body
    );
}
